import { useStores } from 'models';
import { 
  convertValue, 
  getDisplayValue, 
  getUnitLabels,
  formatValue,
  UnitSystem,
  scaleDataWithUnits
} from 'util/unitConversion';

/**
 * Custom hook that provides unit conversion functions based on user's unit preference
 * Automatically uses the user's selected unit system from app settings
 */
export const useUnitConversion = () => {
  const { appData } = useStores();
  const userUnits = appData.displaySettings.weatherUnits as UnitSystem;

  return {
    /**
     * Current user's unit system preference
     */
    userUnits,

    /**
     * Convert a value from one unit system to another
     */
    convert: (
      value: number,
      unitType: 'speed' | 'weight' | 'height' | 'temperature',
      from: UnitSystem = 'metric',
      to: UnitSystem = userUnits
    ) => convertValue(value, unitType, from, to),

    /**
     * Get display value with conversion and formatting based on user's preference
     */
    getDisplay: (
      value: number,
      unitType: 'speed' | 'weight' | 'height' | 'temperature',
      sourceUnits: UnitSystem = 'metric'
    ) => getDisplayValue(value, unitType, userUnits, sourceUnits),

    /**
     * Get unit label for the user's preferred system
     */
    getUnitLabel: (unitType: 'speed' | 'weight' | 'height' | 'temperature') => 
      getUnitLabels[unitType](userUnits),

    /**
     * Format a value with appropriate unit for display
     */
    format: (
      value: number,
      unitType: 'speed' | 'weight' | 'height' | 'temperature',
      targetUnits: UnitSystem = userUnits
    ) => formatValue[unitType](value, targetUnits),

    /**
     * Scale distance/height data with unit conversion
     */
    scaleDistance: (value: number | undefined) => {
      if (value === undefined || value === null) return { value: 'N/A', suffix: '' };
      
      let convertedValue = value;
      let suffix = 'm';
      
      if (userUnits === 'imperial') {
        // Convert meters to feet
        convertedValue = value * 3.28084;
        suffix = 'ft';
      }
      
      // Handle small values in metric system (convert to cm)
      if (userUnits === 'metric' && convertedValue < 1) {
        suffix = 'cm';
        convertedValue *= 100;
      }
      // Handle small values in imperial system (convert to inches)  
      else if (userUnits === 'imperial' && convertedValue < 1) {
        suffix = 'in';
        convertedValue *= 12;
      }
      
      return { 
        value: convertedValue.toFixed(2), 
        suffix: ` ${suffix}` 
      };
    },

    /**
     * Scale speed data with unit conversion  
     */
    scaleSpeed: (value: number | undefined) => {
      if (value === undefined || value === null) return { value: 'N/A', suffix: '' };
      
      if (userUnits === 'imperial') {
        const convertedValue = convertValue(value, 'speed', 'metric', 'imperial');
        return { 
          value: convertedValue.toFixed(2), 
          suffix: ' mph' 
        };
      }
      
      return { 
        value: value.toFixed(2), 
        suffix: ' km/h' 
      };
    },

    /**
     * Check if user prefers metric system
     */
    isMetric: userUnits === 'metric',

    /**
     * Check if user prefers imperial system
     */
    isImperial: userUnits === 'imperial'
  };
};
